import type { ClauseOp, CustomField, MasterList, NormalizedTxn, NormalizedTxnWithCustom, PatternAggregate, PatternEvent, PatternScope, RuleAction } from '@kkp-fraud/shared';
import { isSetOp } from '@kkp-fraud/shared';
import { catalogueKeys, t } from './i18n';

/**
 * Browser-side mirror of rule-engine's NORMALIZED_FIELD_KINDS. The engine package is a
 * Node build, so the front end cannot import it; these two lists must stay in step, and
 * `fields.test.ts` is the reminder. The server is still the authority — anything this
 * misses comes back from POST /admin/rules/validate.
 */
export type FieldKind = 'string' | 'number' | 'boolean';
export type FieldName = keyof NormalizedTxn;

export const FIELD_KINDS: Record<FieldName, FieldKind> = {
  card_id: 'string',
  customer_id: 'string',
  transaction_id: 'string',
  channel_id: 'string',
  message_type: 'string',
  pre_auth: 'boolean',
  event_time: 'string',
  event_epoch_ms: 'number',
  local_hour: 'number',
  amount: 'number',
  amount_thb: 'number',
  currency_code_num: 'number',
  merchant_id: 'string',
  merchant_name: 'string',
  merchant_city: 'string',
  merchant_country_num: 'number',
  acquirer_country_num: 'number',
  terminal_id: 'string',
  mcc: 'number',
  txn_type: 'string',
  entry_mode: 'string',
  pin_capable: 'boolean',
  card_present: 'boolean',
  attended: 'boolean',
  is_atm: 'boolean',
  ecommerce: 'boolean',
  three_ds_authenticated: 'boolean',
  network: 'string',
  fraud_score: 'number',
  fraud_score_reason: 'string',
  product: 'string',
};

export type FieldGroup = 'identity' | 'time' | 'amount' | 'merchant' | 'channel' | 'scheme';

/**
 * The dropdown, grouped so a fraud analyst can find a field without knowing the schema.
 * `labelKey` is a catalogue key, resolved with `t()` where the group is drawn — never at module
 * load, which would freeze the language the page happened to boot in.
 */
export const FIELD_GROUPS: { group: FieldGroup; labelKey: string; fields: FieldName[] }[] = [
  {
    group: 'identity',
    labelKey: 'group.identity.label',
    fields: ['card_id', 'customer_id', 'transaction_id', 'channel_id', 'message_type', 'pre_auth'],
  },
  { group: 'time', labelKey: 'group.time.label', fields: ['event_time', 'event_epoch_ms', 'local_hour'] },
  { group: 'amount', labelKey: 'group.amount.label', fields: ['amount', 'amount_thb', 'currency_code_num'] },
  {
    group: 'merchant',
    labelKey: 'group.merchant_terminal.label',
    fields: ['merchant_id', 'merchant_name', 'merchant_city', 'merchant_country_num', 'acquirer_country_num', 'terminal_id', 'mcc'],
  },
  {
    group: 'channel',
    labelKey: 'group.channel.label',
    fields: ['txn_type', 'entry_mode', 'pin_capable', 'card_present', 'attended', 'is_atm', 'ecommerce', 'three_ds_authenticated'],
  },
  { group: 'scheme', labelKey: 'group.scheme.label', fields: ['network', 'fraud_score', 'fraud_score_reason', 'product'] },
];

export const FIELD_NAMES = Object.keys(FIELD_KINDS) as FieldName[];

// ---- Wave 6: custom fields ---------------------------------------------------------------

/**
 * The custom fields (`RuleSet.customFields`) of the set whose rules are on screen — the same
 * "module value + hook" shape as `t()` / `useLocale()`: `useCustomFields()` (hooks/) records its
 * context here on every render of a component that draws field pickers, so the pure helpers
 * below (`kindOf`, `valueInputKind`, `sources.ts`, `blocks/`) resolve a custom name without a
 * parameter threaded through every signature. Standard fields always win a name.
 */
let activeCustom: readonly CustomField[] = [];

export function setActiveCustomFields(list: readonly CustomField[]): void {
  activeCustom = list;
}

export function activeCustomFields(): readonly CustomField[] {
  return activeCustom;
}

/** A standard field's name (own key of `FIELD_KINDS`, never a prototype key). */
export const isStandardField = (name: string): name is FieldName => Object.prototype.hasOwnProperty.call(FIELD_KINDS, name);

/** The active custom field of that name — never one a standard field shadows. */
export function customFieldOf(name: string): CustomField | undefined {
  return isStandardField(name) ? undefined : activeCustom.find((f) => f.name === name);
}

/** A field's kind: the standard table first, then the active custom fields; `undefined` = unknown. */
export function kindOf(field: string): FieldKind | undefined {
  return isStandardField(field) ? FIELD_KINDS[field] : customFieldOf(field)?.kind;
}

/** The active custom field names, as picker values (a clause's `field` holds either kind of name). */
export function customFieldNames(): FieldName[] {
  return activeCustom.filter((f) => !isStandardField(f.name)).map((f) => f.name as FieldName);
}

/** A field's value on a dry-run transaction: the standard field, else `normalized.custom`. */
export function txnFieldValue(txn: NormalizedTxnWithCustom, field: string): unknown {
  return isStandardField(field) ? txn[field] : txn.custom?.[field];
}

/**
 * The fields a pattern rule can filter or aggregate on — the ones kept on past events. Mirror of
 * `HISTORY_FIELDS` in rust/fraud-engine/src/state/types.rs (`fields.test.ts` compares the two):
 * a field outside it is absent on history, so the engine refuses it (`filter_field_not_history`).
 * The pattern editor offers only these, so the refusal is not something a user can pick.
 */
export const HISTORY_FIELDS: readonly FieldName[] = [
  'card_id',
  'customer_id',
  'transaction_id',
  'event_epoch_ms',
  'local_hour',
  'amount_thb',
  'currency_code_num',
  'merchant_id',
  'merchant_country_num',
  'acquirer_country_num',
  'terminal_id',
  'mcc',
  'txn_type',
  'entry_mode',
  'card_present',
  'is_atm',
  'ecommerce',
  'three_ds_authenticated',
  'network',
];
const HISTORY_SET: ReadonlySet<string> = new Set(HISTORY_FIELDS);
export const isHistoryField = (field: string): boolean => HISTORY_SET.has(field);

/**
 * The plain-language hint shown with a field (the dropdown's tooltip, the field table): the
 * catalogue's `field.<name>.desc`, for the fields that have one.
 */
export function fieldHint(field: FieldName): string | undefined {
  const key = `field.${field}.desc`;
  return FIELD_HINT_KEYS.has(key) ? t(key) : undefined;
}

/** A standard field's plain-language name (`field.<name>.label` — every standard field has one). */
export function fieldLabel(field: FieldName): string {
  return t(`field.${field}.label`);
}

/** Every field the catalogue describes — a new `field.<name>.desc` shows up without a code change. */
const FIELD_HINT_KEYS = new Set(catalogueKeys('en').filter((k) => /^field\.[a-z0-9_]+\.desc$/.test(k)));

/** The comparison operators in dropdown order, with the symbol printed after each label. */
export const OPS: { op: ClauseOp; symbol: string | null }[] = [
  { op: 'eq', symbol: '=' },
  { op: 'neq', symbol: '≠' },
  { op: 'gt', symbol: '>' },
  { op: 'gte', symbol: '≥' },
  { op: 'lt', symbol: '<' },
  { op: 'lte', symbol: '≤' },
  { op: 'in', symbol: '∈' },
  { op: 'nin', symbol: '∉' },
  { op: 'between', symbol: null },
  // R7: the value is a Master list id, not a literal — no symbol, the label says "Master list".
  { op: 'in_set', symbol: null },
  { op: 'not_in_set', symbol: null },
];

/** "greater than (>)" — the operator's catalogue label and its symbol. */
export function opLabel(op: ClauseOp): string {
  const symbol = OPS.find((o) => o.op === op)?.symbol;
  const label = t(`op.${op}.label`);
  return symbol ? `${label} (${symbol})` : label;
}

/**
 * Which ops make sense for a field of this kind — mirrors the engine's own checks. A Master
 * list holds strings or numbers, never booleans, so a yes/no field has no set op (R7).
 */
export function opsFor(kind: FieldKind): ClauseOp[] {
  if (kind === 'boolean') return ['eq', 'neq'];
  if (kind === 'string') return ['eq', 'neq', 'in', 'nin', 'in_set', 'not_in_set'];
  return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'nin', 'between', 'in_set', 'not_in_set'];
}

/** How the value editor renders. `set` is a choice of Master list (R7), stored as the list id. */
export type ValueInputKind = 'number' | 'text' | 'boolean' | 'list' | 'pair' | 'set';

export function valueInputKind(field: FieldName, op: ClauseOp): ValueInputKind {
  if (isSetOp(op)) return 'set';
  if (op === 'between') return 'pair';
  if (op === 'in' || op === 'nin') return 'list';
  const kind = kindOf(field);
  if (kind === 'boolean') return 'boolean';
  if (kind === 'number') return 'number';
  return 'text';
}

/**
 * A value that is at least type-correct when the field or op changes under it. A set op
 * starts with no list (`''`) — never an array or a number; the clause editor then picks the
 * first list that fits, when it has the lists (`masterListsFor`).
 */
export function defaultValueFor(field: FieldName, op: ClauseOp): unknown {
  const kind = kindOf(field);
  if (isSetOp(op)) return '';
  if (op === 'between') return [0, 0];
  if (op === 'in' || op === 'nin') return [];
  if (kind === 'boolean') return true;
  if (kind === 'number') return 0;
  return '';
}

/**
 * The Master lists a set clause on `field` may name (R7): a list meant for this field or for
 * no field in particular, holding values of the field's own kind — a string list for a string
 * field, a number list for a number field, none at all for a yes/no field. The engine refuses
 * the rest (`master list 'X' holds number values but merchant_id is string`).
 */
export function masterListsFor(field: FieldName, lists: readonly MasterList[]): MasterList[] {
  const kind = kindOf(field);
  if (kind !== 'string' && kind !== 'number') return [];
  return lists.filter((l) => l.valueType === kind && (!l.field || l.field === field));
}

/**
 * A comma-separated list as typed ⇄ the array the document stores. Shared by the clause
 * rows and the block view so `mcc in 5411, 5541` means one thing, parsed in one place.
 */
export function parseListInput(raw: string, kind: FieldKind): unknown[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => {
      if (kind === 'number') {
        const n = Number(s);
        return Number.isFinite(n) ? n : s;
      }
      if (kind === 'boolean') return s === 'true';
      return s;
    });
}

export function listToInput(value: unknown): string {
  if (!Array.isArray(value)) return '';
  return value.map((v) => String(v)).join(', ');
}

export const ACTIONS: RuleAction[] = ['DECLINE', 'STEP_UP', 'APPROVE_NOTIFY', 'ALERT_ONLY', 'APPROVE'];
export const PRIORITIES = ['P1', 'P2', 'P3', 'P4'] as const;
export const SCOPES: PatternScope[] = ['card_id', 'customer_id', 'merchant_id'];
export const EVENTS: PatternEvent[] = ['approved', 'attempt', 'decline', 'atm_withdrawal', 'refund', 'ecommerce'];
export const AGGREGATES: PatternAggregate[] = ['count', 'sum', 'distinct'];
